/**
 * Credit Memo Editor (SPRINT-25 F008 + GP F008 Auto-Content UX)
 *
 * Editor for a single credit memo within the Deal Financial Hub.
 * Features:
 * - Read-only header summary (auto-populated from consolidated snapshot)
 * - Status dropdown (draft → in_review → approved → submitted)
 * - Prepared By / Reviewed By fields
 * - 6 collapsible sections (CreditMemoSection)
 * - Sections complete progress indicator
 * - Back button to return to memo list
 * - F008: Auto-Populate button, confirmation modal, "Needs review" chip
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { CreditMemoSection, type CreditMemoSectionHandle } from "./credit-memo-section";
import type { DealCreditMemo, DealCreditMemoSection } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreditMemoWithSections extends DealCreditMemo {
  sections: DealCreditMemoSection[];
}

interface CreditMemoEditorProps {
  dealId: string;
  memoId: string;
  onBack: () => void;
}

interface PopulateResult {
  sectionsUpdated: number;
  sectionsSkipped: number;
  snapshotId: string | null;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDecimal(value: string | number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "—";
  return num.toFixed(decimals);
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    draft: "Draft",
    in_review: "In Review",
    approved: "Approved",
    submitted: "Submitted",
  };
  return map[status] ?? status;
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  const map: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
    draft: "secondary",
    in_review: "default",
    approved: "default",
    submitted: "outline",
  };
  return map[status] ?? "secondary";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreditMemoEditor({ dealId, memoId, onBack }: CreditMemoEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sections, setSections] = useState<DealCreditMemoSection[]>([]);
  const [showPopulateModal, setShowPopulateModal] = useState(false);
  const [skipExisting, setSkipExisting] = useState(true);
  const sectionRefsMap = useRef<Map<string, CreditMemoSectionHandle>>(new Map());

  // -----------------------------------------------------------------------
  // Fetch memo (with sections)
  // -----------------------------------------------------------------------

  const { data: memo, isLoading } = useQuery<CreditMemoWithSections>({
    queryKey: [`/api/financial/deals/${dealId}/credit-memos/${memoId}`],
    enabled: !!dealId && !!memoId,
  });

  // Sync sections from server data on first load (and when memo changes from
  // an external refetch) using useEffect to avoid anti-pattern of setting
  // state during render.
  useEffect(() => {
    if (memo?.sections && memo.sections.length > 0 && sections.length === 0) {
      setSections(
        [...memo.sections].sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
        ),
      );
    }
  }, [memo?.sections, sections.length]);

  // Use local sections if populated, otherwise fall back to server data
  const activeSections: DealCreditMemoSection[] =
    sections.length > 0 ? sections : (memo?.sections ?? []);

  // -----------------------------------------------------------------------
  // Update memo mutation
  // -----------------------------------------------------------------------

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<DealCreditMemo>) => {
      const response = await apiRequest(
        "PATCH",
        `/api/financial/deals/${dealId}/credit-memos/${memoId}`,
        data,
      );
      return (await response.json()) as DealCreditMemo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/credit-memos`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/credit-memos/${memoId}`],
      });
      toast({ title: "Credit memo updated", duration: 2000 });
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Could not update memo. Please try again.",
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // F008 AC004: Auto-populate mutation
  // -----------------------------------------------------------------------

  const populateMutation = useMutation({
    mutationFn: async (overwrite: boolean) => {
      const response = await apiRequest(
        "POST",
        `/api/financial/deals/${dealId}/credit-memos/${memoId}/auto-populate`,
        { overwrite },
      );
      return (await response.json()) as PopulateResult;
    },
    onSuccess: (result) => {
      setShowPopulateModal(false);
      // Reset local sections so next render picks up server data
      setSections([]);
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/credit-memos/${memoId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/credit-memos`],
      });
      toast({
        title: "Sections populated",
        description: `${result.sectionsUpdated} updated, ${result.sectionsSkipped} skipped.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Auto-populate failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  function handleStatusChange(newStatus: string) {
    updateMutation.mutate({ status: newStatus as DealCreditMemo["status"] });
  }

  function handleSectionUpdated(updated: DealCreditMemoSection) {
    setSections((prev) =>
      prev.map((s) => (s.id === updated.id ? updated : s)),
    );
  }

  function handleConfirmPopulate() {
    const overwrite = !skipExisting;
    populateMutation.mutate(overwrite);
  }

  const flushAllPendingSaves = useCallback(() => {
    sectionRefsMap.current.forEach((handle) => handle.flushPendingSave());
  }, []);

  function handleOpenPopulateModal() {
    flushAllPendingSaves();
    setSkipExisting(true);
    setShowPopulateModal(true);
  }

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------

  const sectionsToRender = [...activeSections].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );
  const completedCount = sectionsToRender.filter((s) => s.isComplete).length;
  const totalSections = sectionsToRender.length;
  const currentStatus = memo?.status ?? "draft";

  // F008 AC007: Check if any sections are auto-generated
  const hasAutoGeneratedSections = useMemo(
    () => activeSections.some((s) => s.autoGenerated === true),
    [activeSections],
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="panel-credit-memo-editor">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!memo) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="panel-credit-memo-editor">
        Credit memo not found.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="panel-credit-memo-editor">
      {/* Back button + Auto-Populate */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          data-testid="button-back-to-list"
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Memos
        </Button>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenPopulateModal}
            data-testid="button-auto-populate"
            className="gap-1.5"
          >
            <Sparkles className="h-4 w-4" />
            Auto-Populate
          </Button>
        </div>
      </div>

      {/* Header Summary Card */}
      <Card data-testid="memo-header-card">
        <CardHeader className="py-3 px-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-base">{memo.title}</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Credit Memo Summary
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={statusVariant(currentStatus)}>
                {statusLabel(currentStatus)}
              </Badge>
              {/* F008 AC007: Needs review chip */}
              {hasAutoGeneratedSections && (
                <Badge
                  variant="outline"
                  data-testid={`badge-needs-review-${memoId}`}
                  className="border-amber-400 text-amber-700 text-xs"
                >
                  Needs review
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-4 pb-4 space-y-4">
          {/* Client & Deal info */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Client:</span>{" "}
              <span data-testid="memo-header-client-name" className="font-medium">
                {memo.clientName ?? "—"}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Deal:</span>{" "}
              <span className="font-medium">{memo.dealReference ?? "—"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Total Facility:</span>{" "}
              <span className="font-medium">
                {formatCurrency(memo.totalFacilityAmount)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Version:</span>{" "}
              <span className="font-medium">{memo.version ?? 1}</span>
            </div>
          </div>

          <Separator />

          {/* Financial metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-1 text-sm">
            <div>
              <span className="text-muted-foreground block text-xs">Adj. EBITDA</span>
              <span className="font-medium">{formatCurrency(memo.adjustedEbitda)}</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-xs">DSCR</span>
              <div className="flex items-center gap-1">
                <span data-testid="memo-header-dscr" className="font-medium">
                  {formatDecimal(memo.dscr)}x
                </span>
                {memo.passesServiceability !== null &&
                  memo.passesServiceability !== undefined && (
                    <>
                      {memo.passesServiceability ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-destructive" />
                      )}
                    </>
                  )}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground block text-xs">ICR</span>
              <span className="font-medium">{formatDecimal(memo.icr)}x</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-xs">Surplus</span>
              <span className="font-medium">{formatCurrency(memo.surplus)}</span>
            </div>
          </div>

          <Separator />

          {/* Status + prepared by row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Status</span>
              <Select
                value={currentStatus}
                onValueChange={handleStatusChange}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger
                  data-testid="select-memo-status"
                  className="h-8 text-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value="draft"
                    data-testid="select-item-draft"
                  >
                    Draft
                  </SelectItem>
                  <SelectItem
                    value="in_review"
                    data-testid="select-item-in_review"
                  >
                    In Review
                  </SelectItem>
                  <SelectItem
                    value="approved"
                    data-testid="select-item-approved"
                  >
                    Approved
                  </SelectItem>
                  <SelectItem
                    value="submitted"
                    data-testid="select-item-submitted"
                  >
                    Submitted
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Prepared By</span>
              <p className="text-sm font-medium">{memo.preparedBy ?? "—"}</p>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Reviewed By</span>
              <p className="text-sm font-medium">{memo.reviewedBy ?? "—"}</p>
            </div>
          </div>

          {/* Progress indicator */}
          <div
            data-testid="memo-sections-progress"
            className="flex items-center gap-2 text-sm"
          >
            <span className="text-muted-foreground">Sections complete:</span>
            <span className="font-semibold">
              {completedCount}/{totalSections}
            </span>
            {completedCount === totalSections && totalSections > 0 && (
              <Badge variant="default" className="bg-green-600 text-white text-xs">
                Ready to submit
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Accordion type="single" collapsible className="space-y-2">
        {sectionsToRender.map((section) => (
          <CreditMemoSection
            key={section.id}
            ref={(handle) => {
              if (handle) {
                sectionRefsMap.current.set(section.id, handle);
              } else {
                sectionRefsMap.current.delete(section.id);
              }
            }}
            section={section}
            dealId={dealId}
            memoId={memoId}
            onSectionUpdated={handleSectionUpdated}
          />
        ))}
      </Accordion>

      {/* F008 AC004: Auto-Populate Confirmation Modal */}
      <Dialog
        open={showPopulateModal}
        onOpenChange={setShowPopulateModal}
      >
        <DialogContent data-testid="modal-auto-populate">
          <DialogHeader>
            <DialogTitle>Auto-Populate Credit Memo</DialogTitle>
            <DialogDescription>
              This will fill empty sections with data from this deal&apos;s
              serviceability analysis. You can edit all content afterwards.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2">
            <Checkbox
              id="skip-existing"
              data-testid="checkbox-skip-existing"
              checked={skipExisting}
              onCheckedChange={(checked) => setSkipExisting(!!checked)}
            />
            <label htmlFor="skip-existing" className="text-sm cursor-pointer">
              Skip sections that already have content (recommended)
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPopulateModal(false)}
              data-testid="button-cancel-auto-populate"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPopulate}
              disabled={populateMutation.isPending}
              data-testid="button-confirm-auto-populate"
            >
              {populateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Auto-Populate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
